import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase } from './supabaseClient'
import * as api from './api'
import { Telemetry } from './telemetry'

interface AuthContextValue {
  session: Session | null
  loading: boolean
  signInWithPassword: (email: string, password: string) => Promise<{ error: string | null }>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue>({
  session: null,
  loading: true,
  signInWithPassword: async () => ({ error: 'not initialized' }),
  signOut: async () => {},
})

async function identifyChronaUser(): Promise<void> {
  try {
    const s = await api.fetchSession()
    if (s?.chronaUserId) Telemetry.identify(s.chronaUserId)
  } catch {
    // Telemetry must never block features. A failure here just leaves
    // the caller as `anonymous` in PostHog until the next boot.
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) {
        // On /update-password the SDK may have already parsed a recovery
        // token from the URL hash and stored it as the active session.
        // Don't treat that transient recovery session as a normal sign-in —
        // UpdatePasswordPage manages its own session lifecycle.
        if (window.location.pathname === '/update-password') {
          setLoading(false)
          return
        }
        setSession(data.session)
        setLoading(false)
        if (data.session) {
          // Existing session restored on page load — resolve the Chrona
          // user id so PostHog can merge events with any other Chrona
          // surface the same user touches (A2F-434). Fires on every
          // authenticated mount; /ribbon/session is idempotent.
          void identifyChronaUser()
        }
      }
    })
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, next) => {
      // PASSWORD_RECOVERY: do not surface the transient recovery session as
      // a normal authenticated session. UpdatePasswordPage handles it directly.
      if (event === 'PASSWORD_RECOVERY') {
        setLoading(false)
        return
      }
      // After updateUser() succeeds Supabase fires SIGNED_IN on
      // /update-password. Swallow it here — the page navigates away after
      // success and we don't want to flash-authenticate before the redirect.
      if (event === 'SIGNED_IN' && window.location.pathname === '/update-password') {
        return
      }
      setSession(next)
      if (event === 'SIGNED_IN' && next) {
        void identifyChronaUser()
      } else if (event === 'SIGNED_OUT') {
        Telemetry.reset()
      }
    })
    return () => {
      cancelled = true
      subscription.unsubscribe()
    }
  }, [])

  const signInWithPassword = useCallback(
    async (email: string, password: string) => {
      const { error } = await supabase.auth.signInWithPassword({ email, password })
      return { error: error?.message ?? null }
    },
    [],
  )

  const signOut = useCallback(async () => {
    await supabase.auth.signOut()
  }, [])

  return (
    <AuthContext.Provider value={{ session, loading, signInWithPassword, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
